import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { Link } from "wouter";
import {
  OPTIONAL_CONSENT_CATEGORIES,
  OptionalConsentCategory,
  allDenied,
  allGranted,
} from "@shared/consent";
import { useConsent } from "../../lib/consent";
import { useT } from "../../lib/i18n";

/**
 * The cookie banner and its preference centre.
 *
 * Rendered once near the app root. The banner only appears where prior opt-in
 * is required and no choice has been recorded; the preference centre is
 * reachable everywhere, from the "Manage Cookie Preferences" links in the
 * footer and in Settings, because withdrawal has to be as easy as consent.
 *
 * Accept and reject are given equal visual weight. A reject button that is
 * harder to find than accept is the single most commonly cited dark pattern in
 * DPA enforcement decisions, and it is what most banners get wrong.
 */

type OptionalCategory = OptionalConsentCategory;

function CategoryRow({
  title,
  description,
  control,
  as: Wrapper = "label",
}: {
  title: string;
  description: string;
  control: React.ReactNode;
  as?: "label" | "div";
}) {
  return (
    <Wrapper
      className={`flex items-start gap-3 py-3 ${Wrapper === "label" ? "cursor-pointer" : ""}`}
    >
      <span className="mt-0.5 shrink-0">{control}</span>
      <span className="flex flex-col gap-1">
        <span className="font-semibold">{title}</span>
        <span className="text-sm text-dark-muted leading-relaxed">{description}</span>
      </span>
    </Wrapper>
  );
}

function PreferencesModal() {
  const { state, preferencesOpen, closePreferences, savePreferences } = useConsent();
  const { t } = useT();

  const [draft, setDraft] = useState<Record<OptionalCategory, boolean>>({
    functional: state.functional,
    analytics: state.analytics,
    marketing: state.marketing,
  });

  // Re-seed each time it opens so a previous unsaved edit isn't shown as if it
  // were the current setting.
  useEffect(() => {
    if (preferencesOpen) {
      setDraft({
        functional: state.functional,
        analytics: state.analytics,
        marketing: state.marketing,
      });
    }
  }, [preferencesOpen, state.analytics, state.functional, state.marketing]);

  if (!preferencesOpen) return null;

  return (
    <div className="modal modal-open z-[10000]" onClick={closePreferences}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="modal-box relative card max-w-lg"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t("cookies.preferences.title")}
      >
        <h3 className="font-bold text-xl mb-2">{t("cookies.preferences.title")}</h3>
        <p className="text-sm text-dark-muted leading-relaxed">
          {t("cookies.preferences.intro")}
        </p>

        <div className="divide-y divide-divider dark:divide-neutral-700 mt-4">
          {/* A disabled, permanently-checked toggle reads as a broken control.
              A plain "Always on" badge says the same thing without inviting a
              click that will never do anything. */}
          <CategoryRow
            as="div"
            title={t("cookies.categories.necessary.title")}
            description={t("cookies.categories.necessary.description")}
            control={
              <span className="btn-inline pointer-events-none">
                {t("cookies.categories.necessary.alwaysOn")}
              </span>
            }
          />
          {OPTIONAL_CONSENT_CATEGORIES.map((category) => (
            <CategoryRow
              key={category}
              title={t(`cookies.categories.${category}.title`)}
              description={t(`cookies.categories.${category}.description`)}
              control={
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={draft[category]}
                  aria-label={t(`cookies.categories.${category}.title`)}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, [category]: e.target.checked }))
                  }
                />
              }
            />
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mt-6">
          <button
            className="btn btn-primary flex-1"
            onClick={() => savePreferences(draft)}
          >
            {t("cookies.buttons.save")}
          </button>
          <button
            className="btn btn-secondary flex-1"
            onClick={() => savePreferences(allGranted())}
          >
            {t("cookies.buttons.acceptAll")}
          </button>
          <button
            className="btn btn-secondary flex-1"
            onClick={() => savePreferences(allDenied())}
          >
            {t("cookies.buttons.rejectAll")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function Banner() {
  const { resolved, needsDecision, preferencesOpen, acceptAll, rejectAll, openPreferences } =
    useConsent();
  const { t } = useT();

  // `resolved` gates the entrance: rendering before the region is known would
  // show a banner to everyone for a moment and then snatch it back.
  const visible = resolved && needsDecision && !preferencesOpen;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-x-0 bottom-0 z-[9998] p-3 sm:p-4 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
          role="dialog"
          aria-live="polite"
          aria-label={t("cookies.banner.title")}
        >
          <div className="card mx-auto max-w-3xl p-4 sm:p-5 shadow-xl border border-divider dark:border-neutral-700 bg-white dark:bg-neutral-900">
            <div className="flex items-start gap-3">
              <Cookie className="w-6 h-6 shrink-0 text-primary-500 mt-0.5" />
              <div className="flex-1">
                <h2 className="font-bold text-base sm:text-lg">
                  {t("cookies.banner.title")}
                </h2>
                <p className="text-sm text-dark-muted leading-relaxed mt-1">
                  {t("cookies.banner.body")}{" "}
                  <Link href="/privacy" className="underline hover:text-primary-500">
                    {t("cookies.banner.privacyLink")}
                  </Link>
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button className="btn btn-primary flex-1" onClick={acceptAll}>
                {t("cookies.buttons.acceptAll")}
              </button>
              {/* btn-secondary, not btn-ghost: reject has to carry the same
                  visual weight as accept. A reject button styled as a text link
                  is the single most-cited dark pattern in DPA decisions. */}
              <button className="btn btn-secondary flex-1" onClick={rejectAll}>
                {t("cookies.buttons.rejectAll")}
              </button>
              <button className="btn btn-ghost flex-1" onClick={openPreferences}>
                {t("cookies.buttons.manage")}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function CookieConsent() {
  return (
    <>
      <Banner />
      <PreferencesModal />
    </>
  );
}

export default CookieConsent;
